import {
	Component,
	inject,
	input,
	OnInit,
	TemplateRef,
	ViewContainerRef,
	viewChild
} from '@angular/core';
import { merge, Observable, partition, timer } from 'rxjs';
import { debounce, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { LoadingInterceptor } from './interceptors/loading.interceptor';
import { LoadingService } from './services/loading.service';

@Component({
	selector: 'ngx-loading',
	templateUrl: './loading.component.html',
	styleUrls: ['./loading.component.scss'],
	standalone: true
})
export class LoadingComponent implements OnInit {
	#viewContainer = inject(ViewContainerRef);
	#loadingInterceptor = inject(LoadingInterceptor);
	#loadingSvc = inject(LoadingService);

	isVisible$!: Observable<boolean>;

	readonly _templateRef = viewChild<TemplateRef<any>>('templateRef');
	readonly vertical = input<'center' | 'end' | 'start'>('center');
	readonly horizontal = input<'center' | 'end' | 'start'>('center');
	readonly size = input<[string, string]>(['128px', '128px']);
	readonly backdrop = input(true);
	readonly debounceDelay = input(0);
	readonly entryComponent = input<any>(null);
	readonly extraDuration = input(0);
	readonly filteredHeaders = input<string[]>([]);
	readonly filteredMethods = input<string[]>([]);
	readonly filteredUrlPatterns = input<string[]>([]);
	readonly minDuration = input(0);
	readonly opacity = input('.7');
	readonly backdropBackgroundColor = input('#f1f1f1');

	private visibleUntil = Date.now();

	/** Inserted by Angular inject() migration for backwards compatibility */
	constructor(...args: unknown[]);

	constructor() {}

	ngOnInit(): void {
		this.initIsvisibleObservable();
		// this.nullifySpinnerIfEntryComponentIsDefined();
		this.initFilters();
	}

	private initIsvisibleObservable(): void {
		const [showSpinner$, hideSpinner$] = partition(
			this.#loadingInterceptor.pendingRequestsStatus$,
			(h) => h
		);

		this.isVisible$ = merge(
			this.#loadingInterceptor.pendingRequestsStatus$.pipe(
				switchMap(() =>
					showSpinner$.pipe(debounce(() => timer(this.debounceDelay())))
				)
			),
			showSpinner$.pipe(
				switchMap(() =>
					hideSpinner$.pipe(debounce(() => this.getVisibilityTimer$()))
				)
			),
			this.#loadingSvc.visibility$
		).pipe(
			distinctUntilChanged(),
			tap((h: boolean) => this.updateExpirationDelay(h))
		);

		this.isVisible$.subscribe((value) => {
			if (value) {
				this.#viewContainer.createEmbeddedView(this._templateRef());
			} else {
				this.#viewContainer.clear();
			}
		});
	}

	// private nullifySpinnerIfEntryComponentIsDefined(): void {
	//     if (this.entryComponent) {
	//         this.spinner = null;
	//     }
	// }

	private initFilters(): void {
		this.initFilteredUrlPatterns();
		this.initFilteredMethods();
		this.initFilteredHeaders();
	}

	private initFilteredUrlPatterns(): void {
		const filteredUrlPatterns = this.filteredUrlPatterns();
		if (!!filteredUrlPatterns.length) {
			filteredUrlPatterns.forEach((e) =>
				this.#loadingInterceptor.filteredUrlPatterns.push(new RegExp(e))
			);
		}
	}

	private initFilteredMethods(): void {
		this.#loadingInterceptor.filteredMethods = this.filteredMethods();
	}

	private initFilteredHeaders(): void {
		this.#loadingInterceptor.filteredHeaders = this.filteredHeaders();
	}

	private updateExpirationDelay(showSpinner: boolean): void {
		if (showSpinner) {
			this.visibleUntil = Date.now() + this.minDuration();
		}
	}

	private getVisibilityTimer$(): Observable<number> {
		return timer(
			Math.max(this.extraDuration(), this.visibleUntil - Date.now())
		);
	}
}
